import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EmbeddedViewRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    TemplateRef,
    ViewContainerRef,
    NgZone,
    ViewChild,
    ElementRef,
    OnChanges
} from '@angular/core';
import Popper from 'popper.js';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';
import { FormControl } from '@angular/forms';
import { fromEvent } from 'rxjs';
import { untilDestroyed } from 'ngx-take-until-destroy';

@Component({
    selector: 'label-select',
    templateUrl: './label-select.component.html',
    styleUrls: ['./label-select.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LabelSelectComponent implements OnInit, OnDestroy, OnChanges {

    @ViewChild('origin', { static: true}) componentOrigin: ElementRef;

    @Input() label = 'Label';
    @Input() placeholder = 'Select';
    @Input() searchPlaceholder = 'Search...';
    @Input() textName = 'text';
    @Input() valueName = 'value';
    @Input() options = [];
    @Output() selectChange = new EventEmitter();
    @Output() closed = new EventEmitter();

    modelValue: any;

    @Output() modelChange: EventEmitter<boolean> = new EventEmitter();

    @Input() get model(): boolean {
        return this.modelValue;
    }

    set model(value: boolean) {
        this.modelValue = value;
        this.modelChange.emit(this.modelValue);
    }

    visibleOptionsValue = 4;

    get visibleOptions() {
        if (this.options.length === 0) {
            return 1;
        }

        if (this.options.length >= this.visibleOptionsValue) {
            return this.visibleOptionsValue;
        }

        return this.options.length;
    }

    set visibleOptions(value: number) {
        this.visibleOptionsValue = value;
    }

    searchControl = new FormControl();
    totalItems = 0;

    private view: EmbeddedViewRef<any>;
    private popperRef: Popper;
    private originalOptions = [];

    optionWidth = '0px';

    constructor(private vcr: ViewContainerRef, private zone: NgZone, private cdr: ChangeDetectorRef) { }

    get isOpen() {
        return !!this.popperRef;
    }

    ngOnInit() {
        this.originalOptions = [...this.options];
        if (this.model !== undefined) {
            this.model = this.options.find(currentOption => currentOption[this.valueName] === this.model[this.valueName]);
        }

        this.searchControl.valueChanges
            .pipe(
                debounceTime(300),
                untilDestroyed(this)
            )
            .subscribe(term => this.search(term));

        this.optionWidth = `${this.componentOrigin.nativeElement.offsetWidth - 20}px`;
    }

    ngOnChanges(changes: import('@angular/core').SimpleChanges): void {
        this.originalOptions = [...this.options];
    }

    get text() {
        return this.model ? this.model[this.textName] : this.placeholder;
    }

    open(dropdownTpl: TemplateRef<any>, origin: HTMLElement) {
        if (!this.isOpen) {
            this.view = this.vcr.createEmbeddedView(dropdownTpl);
            const dropdown = this.view.rootNodes[0];

            document.body.appendChild(dropdown);
            dropdown.style.width = `${origin.offsetWidth}px`;

            this.zone.runOutsideAngular(() => {
                this.popperRef = new Popper(origin, dropdown, {
                    removeOnDestroy: true
                });
            });

            this.handleClickOutside();
        }
    }

    close() {
        this.closed.emit();
        this.popperRef.destroy();
        this.view.destroy();
        this.searchControl.patchValue('');
        this.view = null;
        this.popperRef = null;
    }

    select(option) {
        this.model = option;
        this.selectChange.emit(option);
    }

    isActive(option) {
        if (!this.model) {
            return false;
        }
        return option[this.valueName] === this.model[this.valueName];
    }

    search(value: string) {
        this.options = this.originalOptions.filter(option => option[this.textName].includes(value));
        requestAnimationFrame(() => (this.visibleOptions = this.options.length || 1));
    }

    private handleClickOutside() {
        fromEvent(document, 'click')
            .pipe(
                filter(({ target }) => {
                    const origin = this.popperRef.reference as HTMLElement;
                    return origin.contains(target as HTMLElement) === false;
                }),
                takeUntil(this.closed)
            )
            .subscribe(() => {
                this.close();
                this.cdr.detectChanges();
            });
    }

    ngOnDestroy() { }
}
